import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface RegistrationInfo {
    moclient_hash: string;
    device_name: string;
    timestamp: number;
    server_url: string;
}

interface QrScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onScanSuccess: (info: RegistrationInfo) => void;
}

const QrScannerModal: React.FC<QrScannerModalProps> = ({ isOpen, onClose, onScanSuccess }) => {
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const closingRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [scanning, setScanning] = useState(false);
    const [idle, setIdle] = useState(true);

    const parseQrResult = useCallback((decodedText: string): boolean => {
        try {
            const info: RegistrationInfo = JSON.parse(decodedText);
            if (!info.moclient_hash || !info.device_name || !info.server_url) {
                setError('QR 코드에 유효한 등록 정보가 없습니다');
                return false;
            }
            if (!info.moclient_hash.startsWith('MoClient-')) {
                setError('MoClient 해시 형식이 올바르지 않습니다');
                return false;
            }
            closingRef.current = true;
            onScanSuccess(info);
            return true;
        } catch {
            setError('QR 코드가 유효한 JSON이 아닙니다');
            return false;
        }
    }, [onScanSuccess]);

    const stopScanner = useCallback(async () => {
        if (scannerRef.current) {
            try { await scannerRef.current.stop(); } catch { /* ignore */ }
            try { scannerRef.current.clear(); } catch { /* ignore */ }
            scannerRef.current = null;
        }
    }, []);

    // Cleanup on modal close
    useEffect(() => {
        if (!isOpen) {
            stopScanner();
            setScanning(false);
            setIdle(true);
            setError(null);
            closingRef.current = false;
        }
    }, [isOpen, stopScanner]);

    // Called directly from button tap — user gesture required by iOS Safari
    const startCamera = useCallback(async () => {
        setIdle(false);
        setError(null);

        // Step 1: Request camera permission directly in user gesture context
        // This is critical for iOS Safari — getUserMedia MUST be in the tap callstack
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
        } catch (err: any) {
            const msg = err?.name === 'NotAllowedError'
                ? '카메라 접근이 거부되었습니다. Safari 설정에서 카메라를 허용해주세요.'
                : err?.name === 'NotFoundError'
                ? '카메라를 찾을 수 없습니다.'
                : `카메라 오류: ${err?.name || ''} ${err?.message || ''}`;
            setError(msg);
            setIdle(true);
            return;
        }

        // Stop the permission stream — html5-qrcode will open its own
        stream.getTracks().forEach(t => t.stop());

        // Step 2: Now start html5-qrcode (permission already granted)
        try {
            const html5QrCode = new Html5Qrcode('qr-reader');
            scannerRef.current = html5QrCode;

            await html5QrCode.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 250, height: 250 } },
                (decodedText: string) => {
                    if (closingRef.current) return;
                    if (parseQrResult(decodedText)) {
                        stopScanner().then(() => setScanning(false));
                    }
                },
                () => {}
            );
            setScanning(true);
        } catch (err: any) {
            setError(`QR 스캐너 오류: ${err?.message || String(err)}`);
            setIdle(true);
        }
    }, [parseQrResult, stopScanner]);

    const handleClose = async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        await stopScanner();
        setScanning(false);
        setError(null);
        setIdle(true);
        closingRef.current = false;
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            style={{ zIndex: 10000 }}
            onClick={handleClose}
        >
            <div className="bg-white p-6 max-w-md w-full rounded-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">QR 코드 스캔</h2>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Camera stream area */}
                <div
                    id="qr-reader"
                    style={{ width: '100%', minHeight: idle ? 0 : 300, display: idle ? 'none' : 'block' }}
                />

                {/* Start button */}
                {idle && (
                    <div className="text-center py-8">
                        <div className="mb-4">
                            <svg className="w-16 h-16 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            MoClient 화면의 QR 코드를 스캔합니다
                        </p>
                        <button
                            onClick={startCamera}
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
                        >
                            카메라 시작
                        </button>
                    </div>
                )}

                {error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                {scanning && (
                    <div className="mt-4 text-center">
                        <p className="text-sm text-gray-600">MoClient QR 코드를 스캔 중...</p>
                    </div>
                )}

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded"
                    >
                        취소
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QrScannerModal;
